import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useViewportVisibilityForAnimations } from "~/components/1_atoms/useViewportVisibility";

export interface CarouselVariableProps {
  /**
   * Array of image objects with src and alt text
   */
  images: Array<{
    src: string;
    alt: string;
  }>;
  /**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * CarouselVariable - An infinite carousel component with variable image heights
 *
 * Features:
 * - True infinite carousel with seamless transitions (no jumps)
 * - 5 images (desktop) or 3 images (mobile) with variable height hierarchy
 * - Center image tallest, side images with decreasing heights
 * - Smooth sliding transitions with hardware-accelerated transforms
 * - Continuous image positioning - no glitches or flashes
 * - Responsive design maintaining height hierarchy on all devices
 * - Viewport visibility animations using useViewportVisibilityForAnimations
 * - Dot pagination as visual indicators showing current position
 * - Navigation buttons disabled during transitions to prevent rapid clicking
 * - Follows Tennis Gourmet design system with tg- prefixes
 * - TypeScript interface for props validation
 * - Accessibility support with proper ARIA labels
 * - Performance optimized with CSS transforms and smooth transitions
 *
 * True Infinite Behavior:
 * - Creates extended array with duplicated images for seamless looping
 * - Uses continuous positioning system to eliminate visual jumps
 * - Smooth transitions in both directions (next/previous)
 * - No teleporting or jarring effects when wrapping around
 * - Maintains consistent visual flow regardless of navigation direction
 *
 * @example
 * ```tsx
 * const carouselImages = [
 *   { src: "/images/carousel/image1.webp", alt: "Image 1" },
 *   { src: "/images/carousel/image2.webp", alt: "Image 2" },
 *   { src: "/images/carousel/image3.webp", alt: "Image 3" },
 *   { src: "/images/carousel/image4.webp", alt: "Image 4" },
 *   { src: "/images/carousel/image5.webp", alt: "Image 5" },
 * ];
 *
 * <CarouselVariable images={carouselImages} className="my-8" />
 * ```
 */
export function CarouselVariable({ images, className = "" }: CarouselVariableProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { ref, isVisible } = useViewportVisibilityForAnimations();

  // Ensure we have at least 5 images for proper carousel functionality
  if (images.length < 5) {
    console.warn("CarouselVariable requires at least 5 images for optimal display");
  }

  // Create extended array for seamless infinite scrolling
  // We create a long enough array to ensure smooth transitions in both directions
  const extendedImages = useMemo(() => {
    if (images.length === 0) return [];

    // Create multiple copies to ensure seamless infinite scrolling
    // Structure: [...images, ...images, ...images, ...images, ...images] (5 full copies)
    // This provides enough buffer for smooth transitions in both directions
    return [...images, ...images, ...images, ...images, ...images];
  }, [images]);

  // Start in the middle set of images (3rd copy) to allow movement in both directions
  const baseOffset = images.length * 2;

  // Current position in the extended array for rendering
  const extendedCurrentIndex = currentIndex + baseOffset;

  // Calculate height based on distance from center for 5-image layout with asymmetric progression
  // Distance mapping: -2 (far-left), -1 (left), 0 (center), 1 (right), 2 (far-right)
  const getHeight = (distance: number) => {
    // Asymmetric height hierarchy - each position has unique height for dynamic visual flow:
    // Position 0 (distance -2, far-left): 45% height - shortest
    // Position 1 (distance -1, left): 70% height - medium-short
    // Position 2 (distance 0, center): 100% height - tallest focal point
    // Position 3 (distance 1, right): 85% height - medium-tall (different from left)
    // Position 4 (distance 2, far-right): 60% height - tall (different from far-left)

    switch (distance) {
      case -2:
        return "h-[50%] bottom-0"; // Far-left: shortest height
      case -1:
        return "h-[60%] bottom-[25%%]"; // Left: medium-short height
      case 0:
        return "h-full bottom-0"; // Center: tallest height (100%)
      case 1:
        return "h-[80%] bottom-[15%]"; // Right: medium-tall height
      case 2:
        return "h-[55%] bottom-[45%]"; // Far-right: tall height

      // For any images beyond the 5-image layout (should not be visible due to opacity)
      default:
        return "h-[30%]"; // Very far images - hidden by opacity
    }
  };

  // Calculate complete transform including translateX and translateY for iOS compatibility
  // Uses CSS custom property for responsive spacing and handles all transforms inline
  const getCompleteTransform = (slideIndex: number, centerIndex: number) => {
    const distance = slideIndex - centerIndex;
    // Combine translateX (horizontal positioning) and translateY (vertical centering)
    // This prevents iOS Safari conflicts between CSS transforms and inline transforms
    return `translateX(calc(-50% + ${distance} * var(--carousel-variable-spacing))) translateY(-50%)`;
  };

  // Calculate z-index based on distance from center
  const getZIndex = (distance: number) => {
    return Math.max(0, 100 - Math.abs(distance));
  };

  // Calculate opacity based on distance (fade out distant slides)
  const getOpacity = (distance: number) => {
    const absDistance = Math.abs(distance);
    if (absDistance <= 1) return 1;
    if (absDistance === 2) return 1; // Keep side images visible
    if (absDistance === 3) return 0.3; // Fade far images
    return 0; // Hide very far images
  };

  // Get all slide properties for rendering using extended array
  const getSlideStyles = () => {
    return extendedImages.map((_, index: number) => {
      const distance = index - extendedCurrentIndex;

      return {
        transform: getCompleteTransform(index, extendedCurrentIndex),
        zIndex: getZIndex(Math.abs(distance)),
        opacity: getOpacity(Math.abs(distance)),
        height: getHeight(distance), // Pass actual distance (not absolute) for asymmetric heights
        isActive: distance === 0,
      };
    });
  };

  const slideStyles = getSlideStyles();

  // Navigation handlers with seamless infinite behavior
  const handleNext = useCallback(() => {
    if (isTransitioning || images.length === 0) return;

    setIsTransitioning(true);

    setCurrentIndex((prevIndex) => prevIndex + 1);

    // Reset transition state and handle position reset after animation completes
    // Use 480ms (slightly longer than CSS 450ms) to ensure animation finishes before state update
    setTimeout(() => {
      setIsTransitioning(false);

      // Reset position invisibly if we've moved too far
      setCurrentIndex((currentIdx) => {
        if (currentIdx >= images.length * 2) {
          return currentIdx - images.length;
        }
        return currentIdx;
      });
    }, 480); // Slightly longer than CSS transition duration (450ms) to prevent iOS bouncing
  }, [isTransitioning, images.length]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning || images.length === 0) return;

    setIsTransitioning(true);

    setCurrentIndex((prevIndex) => prevIndex - 1);

    // Reset transition state and handle position reset after animation completes
    // Use 480ms (slightly longer than CSS 450ms) to ensure animation finishes before state update
    setTimeout(() => {
      setIsTransitioning(false);

      // Reset position invisibly if we've moved too far
      setCurrentIndex((currentIdx) => {
        if (currentIdx <= -images.length) {
          return currentIdx + images.length;
        }
        return currentIdx;
      });
    }, 480); // Slightly longer than CSS transition duration (450ms) to prevent iOS bouncing
  }, [isTransitioning, images.length]);

  return (
    <section
      ref={ref}
      className={`tg-carousel-variable ${className}`}
      role="region"
      aria-label="Variable height image carousel"
    >
      <div
        className={`tg-carousel-variable__wrapper transition-all duration-700 ease-in-out ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
        }`}
      >
        {/* Images Container */}
        <div className="tg-carousel-variable__images">
          {extendedImages.map((image, index) => {
            const style = slideStyles[index];
            const distance = index - extendedCurrentIndex;

            // Only render images that are close enough to be visible
            if (Math.abs(distance) > 3) return null;

            return (
              <div
                key={`${image.src}`}
                className={`tg-carousel-variable__image-container ${style.height} ${
                  style.isActive
                    ? "tg-carousel-variable__image-container--active"
                    : "tg-carousel-variable__image-container--inactive"
                }`}
                style={{
                  transform: style.transform,
                  zIndex: style.zIndex,
                  opacity: style.opacity,
                }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="tg-carousel-variable__image"
                  loading="lazy"
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* Navigation Buttons */}
      <button
        onClick={handlePrevious}
        disabled={isTransitioning}
        className={`tg-carousel-variable__nav tg-carousel-variable__nav--prev ${
          isTransitioning ? "tg-carousel-variable__nav--disabled" : ""
        }`}
        aria-label="Previous image"
        type="button"
      >
        <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
      </button>

      <button
        onClick={handleNext}
        disabled={isTransitioning}
        className={`tg-carousel-variable__nav tg-carousel-variable__nav--next ${
          isTransitioning ? "tg-carousel-variable__nav--disabled" : ""
        }`}
        aria-label="Next image"
        type="button"
      >
        <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
      </button>

      {/* Dot Pagination */}
      <div className="tg-carousel-variable__dots">
        {images.map((image, index) => {
          // Calculate the normalized current index for dot pagination
          const normalizedIndex =
            ((currentIndex % images.length) + images.length) % images.length;

          return (
            <div
              key={`dot-${image.src}`}
              className={`tg-carousel-variable__dot ${
                index === normalizedIndex ? "tg-carousel-variable__dot--active" : ""
              }`}
            />
          );
        })}
      </div>
    </section>
  );
}
